import { z } from "zod";
import { idSchema } from "./issues";

/** What a board's swimlanes group its cards by (BUILD_SPEC §12.2), or none. */
export const LANE_KINDS = ["none", "assignee", "epic", "priority", "type"] as const;
export const laneKindSchema = z.enum(LANE_KINDS);
export type LaneKind = z.infer<typeof laneKindSchema>;

/** What a card can show besides its key and title (BUILD_SPEC §12.2); its project's admins choose which. */
export const CARD_FIELDS = [
  "type",
  "priority",
  "assignee",
  "estimate",
  "labels",
  "due",
  "subtasks",
  "blocked",
] as const;
export const cardFieldSchema = z.enum(CARD_FIELDS);
export type CardField = z.infer<typeof cardFieldSchema>;

export const BOARD_COLUMNS_MAX = 20;
/** How many days a Kanban board's Completed and Cancelled columns keep resolved issues, by default. */
export const DONE_DAYS_DEFAULT = 14;

/**
 * One of a board's columns: its name (null to take its one status's), the statuses it holds, and its work-in-progress
 * limits, each optional.
 */
export const boardColumnSchema = z
  .object({
    /** Kept from the column it was; a new column has none yet. */
    id: idSchema.optional(),
    name: z.string().trim().min(1).max(40).nullable(),
    statusIds: z.array(idSchema).min(1).max(20),
    min: z.number().int().min(0).max(999).nullable(),
    max: z.number().int().min(1).max(999).nullable(),
  })
  .strict()
  .refine((column) => column.min === null || column.max === null || column.min <= column.max, {
    message: "A column's minimum can't be above its maximum",
    path: ["min"],
  });

/** A board's settings (BUILD_SPEC §12.2), as its project's admins set them. */
export const boardSettingsSchema = z
  .object({
    revision: z.number().int().positive().optional(),
    columns: z.array(boardColumnSchema).min(1).max(BOARD_COLUMNS_MAX),
    swimlanes: laneKindSchema,
    cardFields: z.array(cardFieldSchema).max(CARD_FIELDS.length),
    doneDays: z.number().int().min(1).max(90),
  })
  .strict();

export type BoardColumnInput = z.infer<typeof boardColumnSchema>;
export type BoardSettingsInput = z.infer<typeof boardSettingsSchema>;
